import { useIsMobileHook } from '@teliaee/telia-common-components';
import { TranslationsContext } from '@teliaee/telia-translate';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { AnalyticsProvider, Button, Container, Grid, GridColumn, Heading, Separator, Text } from 'telia-front-react';
import Config from '../Config';
import LanguageStore from '../stores/LanguageStore';
import RouteStore from '../stores/RouteStore';
import SearchStore, { ESHOP_CATEGORY } from '../stores/SearchStore';
import EShopSearchResults from './EShopSearchResults';
import SearchInput from './SearchInput';
import SearchResults from './SearchResults';
import SearchResultsSortBar from './SearchResultsSortBar';

export interface SiteSearchProps {
  isMobile?: boolean;
  routeStore?: RouteStore;
  searchStore?: SearchStore;
  languageStore?: LanguageStore;
}

const SiteSearch = (props: SiteSearchProps): JSX.Element => {
  const { isMobile } = useIsMobileHook();

  return <SiteSearchInner {...props} isMobile={isMobile} />;
};

@observer
class SiteSearchInner extends React.Component<SiteSearchProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  private readonly initialSearch: string = '';

  constructor(props: SiteSearchProps) {
    super(props);
    const {
      routeStore: { decodedParams }
    } = this.props;
    this.initialSearch = decodedParams?.query;
  }

  public componentWillUnmount() {
    const {
      searchStore: { resetSearchWrappersAndQuery }
    } = this.props;
    resetSearchWrappersAndQuery();
  }

  private renderRecommendedSubpages = () => {
    const currentLanguage = this.props.languageStore.currentLanguage;
    return (
      <>
        <Grid gutter="smedium" verticalGutter="none">
          <GridColumn width={['xs']}>{this.context.translate('search.suggested.subPages')}</GridColumn>
          {this.props.searchStore.subPageCategories.map((category, index) => {
            return (
              <GridColumn width={['xs']} key={index}>
                <Button text={category.name[currentLanguage]} url={`${Config.teliaShopUrl}/${category.url}`} link={true} />
              </GridColumn>
            );
          })}
        </Grid>
        <Separator color="invisible" spacing="small" />
      </>
    );
  };

  private renderSearchResults = () => {
    if (this.props.searchStore.isOnlyShopSearch) {
      return (
        <Grid align={['center-xs']}>
          <GridColumn>
            <Separator color="invisible" spacing="xsmall" />
            {!!this.props.searchStore.subPageCategories?.length && this.renderRecommendedSubpages()}
            <SearchResultsSortBar category={ESHOP_CATEGORY} isMobile={this.props.isMobile} />
            <EShopSearchResults isOnlyEshopResults={true} isMobile={this.props.isMobile} />
          </GridColumn>
        </Grid>
      );
    }
    return <SearchResults isMobile={this.props.isMobile} recommendedSubPages={this.renderRecommendedSubpages()} />;
  };

  public render() {
    const translate = this.context.translate;
    return (
      <AnalyticsProvider group="Site Search">
        <Container>
          <Text>
            <Text className="text-center">
              <Heading id="searchHeading" element="h1" className="h2">
                {this.props.searchStore.isOnlyShopSearch ? translate('search.eShop.heading') : translate('search.searchResults')}
              </Heading>
              <Separator color="invisible" spacing="small" />
              <SearchInput initialSearch={this.initialSearch} />
            </Text>
            <Separator color="invisible" spacing="small" />
          </Text>
          {this.renderSearchResults()}
        </Container>
      </AnalyticsProvider>
    );
  }
}

export default inject('routeStore', 'searchStore', 'languageStore')(SiteSearch);
